import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Building2,
  LayoutDashboard,
  User,
  KeyRound,
  Folder,
  FileText,
  LogOut,
  LogIn,
  Home,
  Menu as MenuIcon,
  DollarSign,
  HelpCircle,
  ShieldCheck,
  BarChart3,
  MessageSquare,
  Receipt,
} from "lucide-react";
import { useUser } from "../Context/UserContext";
import { useCompany } from "../Context/CompanyContext";
import NotificationsButton from "../Notificaciones/NotificationsButton";
import companyData from "../Data/CompanyData";
import categories from "../Data/Categories";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";

const Header = () => {
  const { user, logout, isLoading } = useUser();
  const navigate = useNavigate();
  const { companies } = useCompany();
  const hasCompany = companies && companies.company;
  const isLector = companies?.company?.tipo_cuenta === "Lector";
  const isStaff = user?.user?.correo?.toLowerCase() === "sveravaldivia95@gmail.com";
  const navMenuItemClass =
    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-foreground outline-none transition-colors hover:bg-muted";

  if (isLoading) {
    return null;
  }

  const handleLicitacionesClick = (path) => {
    if (!user && path === "/create-tender-step-1") {
      navigate("/login");
    } else {
      navigate(path);
    }
  };

  const handleCatalogoClick = (categoria = "") => {
    if (!user) {
      navigate("/login");
    } else if (categoria) {
      navigate("/catalogo", { state: { categoria } });
    } else {
      navigate("/catalogo");
    }
  };

  const handleCompanyRegisterClick = () => {
    navigate(user ? "/company-register" : "/login");
  };

  const handleViewProfile = () => {
    if (user) {
      navigate(`/profile/${user.user.id_user}/${user.user.nombre}-${user.user.apellido}`);
    } else {
      navigate("/login");
    }
  };

  const handleViewMenu = () => {
    if (user) {
      navigate(`/menu/${user.user.id_user}/${user.user.nombre}-${user.user.apellido}`);
    } else {
      navigate("/login");
    }
  };

  const handleLogout = async () => {
    navigate("/");
    await logout();
  };

  const handleNavigation = (path, state = null) => {
    if (
      !user &&
      ["/create-tender-step-1", "/catalogo", "/company-register"].includes(path)
    ) {
      navigate("/login");
    } else if (state) {
      navigate(path, { state });
    } else {
      navigate(path);
    }
  };

  // Menú de cuenta: la misma fuente de datos alimenta el dropdown de escritorio
  // y el panel móvil, para no reimplementar la lista dos veces.
  const accountMenuSections = user
    ? [
        {
          title: "Usuarios",
          items: [
            { label: "Menú de usuario", icon: LayoutDashboard, onClick: handleViewMenu },
            { label: "Ver perfil", icon: User, onClick: handleViewProfile },
            { label: "Cambiar contraseña", icon: KeyRound, onClick: () => handleNavigation("/changepassword") },
          ],
        },
        {
          title: "Empresa",
          items: [
            { label: "Catálogo proveedores", icon: Search, onClick: () => handleNavigation("/catalogo") },
            !hasCompany && {
              label: "Registrar Empresa",
              icon: Plus,
              onClick: handleCompanyRegisterClick,
            },
            hasCompany && {
              label: "Mi empresa",
              icon: Building2,
              onClick: () =>
                handleNavigation(
                  `/company-profile/${companies?.company?.id_company}/${companies?.company?.nombre_empresa}`
                ),
            },
          ].filter(Boolean),
        },
        {
          title: "Licitaciones",
          items: [
            { label: "Buscar licitaciones", icon: Search, onClick: () => handleLicitacionesClick("/licitaciones") },
            !isLector && { label: "Crear licitación", icon: FileText, onClick: () => handleLicitacionesClick("/create-tender-step-1") },
            { label: "Licitaciones propias", icon: LayoutDashboard, onClick: () => handleNavigation("/mis-licitaciones") },
            { label: "Postulaciones propias", icon: Folder, onClick: () => handleNavigation("/mis-postulaciones") },
          ].filter(Boolean),
        },
        isStaff && {
          title: "Admin",
          items: [
            { label: "Visitas del sitio", icon: BarChart3, onClick: () => handleNavigation("/staff/visitas") },
            { label: "Métricas", icon: BarChart3, onClick: () => handleNavigation("/staff/metricas") },
            { label: "Conversaciones", icon: MessageSquare, onClick: () => handleNavigation("/staff/conversaciones") },
            { label: "Empresas", icon: ShieldCheck, onClick: () => handleNavigation("/staff/empresas") },
            { label: "Pagos pendientes", icon: Receipt, onClick: () => handleNavigation("/staff/pagos-pendientes") },
          ],
        },
      ].filter(Boolean)
    : [];

  const guestMenuItems = [
    { label: "Registrar Empresa", icon: Plus, onClick: handleCompanyRegisterClick },
    { label: "Licitaciones", icon: FileText, onClick: () => handleNavigation("/licitaciones") },
    { label: "Proveedores", icon: Building2, onClick: () => handleCatalogoClick() },
    { label: "Ingresa", icon: LogIn, onClick: () => handleNavigation("/login") },
  ];

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-border bg-background px-4 sm:px-6">
      <Link to="/" className="shrink-0">
        <img src={`${process.env.PUBLIC_URL}/fotos/logofin2.png`} alt="Procu" className="h-14 w-auto" />
      </Link>

      {/* Nav de escritorio */}
      <nav className="hidden md:flex items-center gap-1">
        {!hasCompany && (
          <Button onClick={handleCompanyRegisterClick}>
            <Plus className="h-4 w-4" /> Registrar Empresa
          </Button>
        )}

        <div className="group relative">
          <Button variant="ghost" onClick={() => handleLicitacionesClick("/licitaciones")}>
            Licitaciones
          </Button>
          <div
            className="invisible absolute left-0 top-full z-50 min-w-[10rem] overflow-hidden rounded-md
              border border-border bg-background p-1 text-foreground shadow-lg opacity-0 transition-opacity
              group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
          >
            <button
              className={navMenuItemClass}
              onClick={() => handleLicitacionesClick("/licitaciones")}
            >
              <Search className="h-4 w-4" /> Buscar licitaciones
            </button>
            {!isLector && (
              <button
                className={navMenuItemClass}
                onClick={() => handleLicitacionesClick("/create-tender-step-1")}
              >
                <Plus className="h-4 w-4" /> Crear licitación nueva
              </button>
            )}
          </div>
        </div>

        <div className="group relative">
          <Button variant="ghost" onClick={() => handleCatalogoClick()}>
            Proveedores
          </Button>
          <div
            className="invisible absolute left-0 top-full z-50 max-h-96 w-[28rem] overflow-y-auto rounded-md
              border border-border bg-background p-1 text-foreground shadow-lg opacity-0 transition-opacity
              group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
          >
            <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
              {[
                ...categories.filter((cat) => cat.label !== "Otro Servicio"),
                ...companyData.sectores.slice(0, 10),
              ].map((item, index) => (
                <button
                  key={index}
                  className={navMenuItemClass}
                  onClick={() => handleCatalogoClick(item.label)}
                >
                  <span>{item.icon}</span> {item.label}
                </button>
              ))}
            </div>
            {companyData.sectores.length > 10 && (
              <button className={navMenuItemClass} onClick={() => handleNavigation("/catalogo")}>
                <Search className="h-4 w-4" /> Ver más
              </button>
            )}
          </div>
        </div>

        <Button variant="ghost" onClick={() => navigate("/precios")}>
          Precios
        </Button>
        <Button variant="ghost" onClick={() => navigate("/como-funciona")}>
          Cómo funciona
        </Button>
      </nav>

      <div className="hidden md:flex items-center gap-2">
        {user && (
          <Button
            variant="ghost"
            onClick={() =>
              handleNavigation(`/menu/${user.user.id_user}/${user.user.nombre}-${user.user.apellido}`)
            }
          >
            <Home className="h-4 w-4" /> Menú
          </Button>
        )}

        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                {user.user.nombre.length > 9 ? `${user.user.nombre.slice(0, 9)}...` : user.user.nombre}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[80vh] overflow-y-auto">
              {accountMenuSections.map((section) => (
                <React.Fragment key={section.title}>
                  <DropdownMenuLabel>{section.title}</DropdownMenuLabel>
                  {section.items.map((item) => (
                    <DropdownMenuItem key={item.label} onSelect={item.onClick}>
                      <item.icon className="h-4 w-4" /> {item.label}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                </React.Fragment>
              ))}
              <DropdownMenuItem onSelect={handleLogout}>
                <LogOut className="h-4 w-4" /> Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button onClick={() => handleNavigation("/login")}>
            <LogIn className="h-4 w-4" /> Ingresa
          </Button>
        )}

        {user && <NotificationsButton />}
      </div>

      {/* Nav móvil */}
      <div className="flex md:hidden items-center gap-1">
        {user && <NotificationsButton />}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Abrir menú">
              <MenuIcon className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right">
            <SheetHeader>
              <SheetTitle>Menú</SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col gap-1">
              <button
                onClick={() => navigate("/precios")}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted"
              >
                <DollarSign className="h-4 w-4" /> Precios
              </button>
              <button
                onClick={() => navigate("/como-funciona")}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted"
              >
                <HelpCircle className="h-4 w-4" /> Cómo funciona
              </button>
              {user ? (
                <>
                  {accountMenuSections.map((section) => (
                    <div key={section.title} className="mb-2">
                      <p className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                        {section.title}
                      </p>
                      {section.items.map((item) => (
                        <button
                          key={item.label}
                          onClick={item.onClick}
                          className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted"
                        >
                          <item.icon className="h-4 w-4" /> {item.label}
                        </button>
                      ))}
                    </div>
                  ))}
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted"
                  >
                    <LogOut className="h-4 w-4" /> Cerrar sesión
                  </button>
                </>
              ) : (
                guestMenuItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={item.onClick}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted"
                  >
                    <item.icon className="h-4 w-4" /> {item.label}
                  </button>
                ))
              )}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default Header;
